import type { SvgIconTypeMap } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import BarChartIcon from "@mui/icons-material/BarChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import GroupIcon from "@mui/icons-material/Group";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentsIcon from "@mui/icons-material/Payments";
import {
  isAnyFeatureEnabled,
  isBillingInfoVisible,
  isFeatureEnabled,
  isOrganizationViewEnabled,
  isPaymentSurfaceEnabled,
} from "src/helpers/FeatureCapabilityConfig";
import { BOOKING_FEATURES } from "src/business/bookingScope";
import { Owner } from "src/business/account/owner";

export const NAVIGATION_ENTRIES = {
  RESTAURANT_DASHBOARD: "dashboard",
  RESTAURANT_INFORMATION: "info",
  RESTAURANT_SCHEDULE: "schedule",
  RESTAURANT_GALLERY: "gallery",
  RESTAURANT_MENU: "menu",
  RESTAURANT_DEALS: "deals",
  RESTAURANT_BOOKINGS: "bookings",
  RESTAURANT_TABLE_SCHEDULE: "table-schedule",
  RESTAURANT_ANALYTICS: "analytics",
  ORGANIZATION_MANAGEMENT: "my-organization",
  ADMIN_CONTROL_PANEL: "admin-control-panel",
  RESTAURANT_CONFIGURATION: "configuration",
  RESTAURANT_PAYMENTS: "payments",
};

type NavIcon = OverridableComponent<SvgIconTypeMap>;

// Everything the visibility predicate of a section needs to decide whether the
// current user can see it.
export type NavVisibilityContext = {
  owner: Owner | null;
  restaurantCapabilities: string[] | undefined;
  /**
   * Whether the caller manages at least one subscribed restaurant. `false` only
   * once the list has come back empty — an owner who unsubscribed. See
   * {@link SECTIONS_WITHOUT_RESTAURANT}.
   */
  hasRestaurant: boolean;
};

// Everything needed to decide whether a section maps to the current location.
export type NavMatchContext = {
  pathname: string;
};

export type NavSection = {
  // Stable identifier, also used as the React key.
  name: string;
  icon: NavIcon;
  // i18n key resolved by the renderer.
  labelKey: string;
  // Restaurant-bound sections target the currently selected restaurant: they
  // live under /restaurant/:restaurantId and are disabled until one is picked.
  // Standalone sections (organization, admin) live under /restaurant/:segment.
  restaurantBound: boolean;
  // URL segment appended after the restaurant id (bound) or after "/restaurant"
  // (standalone).
  segment: string;
  isVisible: (context: NavVisibilityContext) => boolean;
};

// Single source of truth for the sidebar: order here is the render order.
export const NAVIGATION_BAR_CONFIGURATION: NavSection[] = [
  {
    // The landing page: visible to anyone signed in, since it gates its own
    // sections rather than being gated as a whole.
    name: NAVIGATION_ENTRIES.RESTAURANT_DASHBOARD,
    icon: DashboardIcon,
    labelKey: "sideBar.dashboard",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_DASHBOARD,
    isVisible: ({ owner }) => !!owner,
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_INFORMATION,
    icon: StorefrontIcon,
    labelKey: "sideBar.restaurantInfo",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_INFORMATION,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled("restaurant_management", restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_SCHEDULE,
    icon: AccessTimeIcon,
    labelKey: "sideBar.openingHours",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_SCHEDULE,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled(
        "opening_hours_management",
        restaurantCapabilities,
        owner,
      ),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_GALLERY,
    icon: PhotoLibraryIcon,
    labelKey: "sideBar.mediaGallery",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_GALLERY,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled(
        "image_gallery_management",
        restaurantCapabilities,
        owner,
      ),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_MENU,
    icon: MenuBookIcon,
    labelKey: "sideBar.menuManagement",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_MENU,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled("menu_management", restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_DEALS,
    icon: LocalOfferOutlinedIcon,
    labelKey: "sideBar.deals",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_DEALS,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled("deals_management", restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_BOOKINGS,
    icon: GroupIcon,
    labelKey: "sideBar.bookings",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_BOOKINGS,
    // Listed for the same-day slice too: that tier's whole booking offer is
    // this page, restricted to today.
    isVisible: ({ owner, restaurantCapabilities }) =>
      isAnyFeatureEnabled(BOOKING_FEATURES, restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_TABLE_SCHEDULE,
    icon: TableRestaurantIcon,
    labelKey: "sideBar.tableSchedule",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_TABLE_SCHEDULE,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled("table_scheduling", restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_ANALYTICS,
    icon: BarChartIcon,
    labelKey: "sideBar.statistics",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_ANALYTICS,
    isVisible: ({ owner, restaurantCapabilities }) =>
      isFeatureEnabled("analytics_dashboard", restaurantCapabilities, owner),
  },
  {
    name: NAVIGATION_ENTRIES.ORGANIZATION_MANAGEMENT,
    icon: BusinessIcon,
    labelKey: "sideBar.myOrganization",
    restaurantBound: false,
    segment: NAVIGATION_ENTRIES.ORGANIZATION_MANAGEMENT,
    isVisible: ({ owner }) => isOrganizationViewEnabled(owner),
  },
  {
    name: NAVIGATION_ENTRIES.ADMIN_CONTROL_PANEL,
    icon: AdminPanelSettingsIcon,
    labelKey: "sideBar.adminControlPanel",
    restaurantBound: false,
    segment: NAVIGATION_ENTRIES.ADMIN_CONTROL_PANEL,
    isVisible: ({ owner }) => !!owner?.admin,
  },
  {
    // Outside the capability and subscription gates on purpose, mirroring the
    // backend's PaymentAccessPolicy: a restaurant that cannot pay must still be
    // able to reach the page that lets it pay. What it does honour is the
    // organization read permission — billing is organization business, and a
    // receptionist has no reason to see what the restaurant pays.
    name: NAVIGATION_ENTRIES.RESTAURANT_PAYMENTS,
    icon: PaymentsIcon,
    labelKey: "sideBar.payments",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_PAYMENTS,
    isVisible: ({ owner }) =>
      isPaymentSurfaceEnabled(owner) && isBillingInfoVisible(owner),
  },
  {
    name: NAVIGATION_ENTRIES.RESTAURANT_CONFIGURATION,
    icon: SettingsIcon,
    labelKey: "sideBar.configuration",
    restaurantBound: true,
    segment: NAVIGATION_ENTRIES.RESTAURANT_CONFIGURATION,
    isVisible: ({ owner }) => !!owner,
  },
];

/**
 * The only sections left standing for an account with no subscribed restaurant:
 * the organization area (its staff and roles outlive a subscription), the admin
 * panel (a platform admin is not a restaurateur), and payments — the page that
 * sells the way back in, and the one place the former subscriber can still read
 * what they paid. Every other section manages a restaurant this account has no
 * subscribed restaurant to apply it to, so listing them would only lead to a
 * locked page.
 */
const SECTIONS_WITHOUT_RESTAURANT: ReadonlySet<string> = new Set([
  NAVIGATION_ENTRIES.ORGANIZATION_MANAGEMENT,
  NAVIGATION_ENTRIES.ADMIN_CONTROL_PANEL,
  NAVIGATION_ENTRIES.RESTAURANT_PAYMENTS,
]);

/** The sections to render, in configuration order. */
export const visibleNavSections = (
  context: NavVisibilityContext,
): NavSection[] =>
  NAVIGATION_BAR_CONFIGURATION.filter(
    (section) =>
      section.isVisible(context) &&
      (context.hasRestaurant || SECTIONS_WITHOUT_RESTAURANT.has(section.name)),
  );

// The slugs sitting where a restaurant id would be: /restaurant/my-organization,
// /restaurant/admin-control-panel…
const STANDALONE_SEGMENTS = new Set(
  NAVIGATION_BAR_CONFIGURATION.filter(
    (section) => !section.restaurantBound,
  ).map((section) => section.segment),
);

export const buildNavPath = (
  section: NavSection,
  restaurantId: string | null,
): string =>
  section.restaurantBound
    ? `/restaurant/${restaurantId}/${section.segment}`
    : `/restaurant/${section.segment}`;

export const isNavSectionDisabled = (
  section: NavSection,
  restaurantId: string | null,
): boolean => section.restaurantBound && !restaurantId;

export const isNavSectionActive = (
  section: NavSection,
  { pathname }: NavMatchContext,
): boolean => {
  // Bound sections live under /restaurant/:id/:segment, standalone ones under
  // /restaurant/:segment — match on the slug either way.
  if (!section.restaurantBound) return pathname.includes(section.segment);
  // A standalone section can own a sub-route whose last slug is also a bound
  // section's — /restaurant/admin-control-panel/payments and the restaurant's
  // own payments page. The restaurant id slot decides which one it is.
  if (STANDALONE_SEGMENTS.has(pathname.split("/")[2] ?? "")) return false;
  // Match on a whole segment: one slug can prefix another, and a bare
  // `includes` would light up both entries.
  const match = `/${section.segment}`;
  return pathname.endsWith(match) || pathname.includes(`${match}/`);
};
